import { setErrorRetry } from '@adv-frontend/error';

import type { AnyEndpointQuery } from './apiSSRModule';
import type { AnyApi } from './buildCreateApi';
import { createQueryRejectedActionMatcher } from './patchApiReducer/actions/createQueryRejectedActionMatcher';

/**
 * Привязывает к ошибкам упавших запросов функцию повтора, которую читают потребители
 * состояния потока через `getErrorRetry()` из `@adv-frontend/error`.
 *
 * Мидлвара работает до редьюсера намеренно: объект ошибки, уже попавший в стор, заморожен
 * immer, и дописать в него что-либо нельзя.
 *
 * @param api – Инстанс RTK Query API.
 * @returns Мидлвара для модульного стора.
 */
export function errorRetryMiddleware(api: AnyApi): AnyApi['middleware'] {
    const isQueryRejectedAction = createQueryRejectedActionMatcher(api);

    return mwApi => next => action => {
        // Запрос, отклоненный по `condition`, ошибку в стор не пишет — размечать нечего.
        if (isQueryRejectedAction(action) && !action.meta.condition) {
            const { endpointName, originalArgs } = action.meta.arg;

            setErrorRetry(action.payload ?? action.error, () => {
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- тип эндпоинта в мидлваре заранее не известен
                const endpoint = api.endpoints[endpointName] as AnyEndpointQuery;

                // Тот же запрос, что делает `queryFlow().refetch()`, но без `trackQuery`:
                // поток его не отслеживает, и `flow.abort()` на него не действует.
                mwApi.dispatch(endpoint.initiate(originalArgs, { forceRefetch: true, subscribe: false }));
            });
        }

        return next(action);
    };
}
